import {
    CreateCounterpartyAddressBody,
    UpdateCounterpartyAddressBody,
} from "../schemas/counterpartyAddress.schema";
import {
    CreateCounterpartyContactBody,
    UpdateCounterpartyContactBody,
} from "../schemas/counterpartyContact.schema";
import {
    changedCounterpartyDetails,
    CounterpartyDetailChanges,
    ExtractedCounterpartyParty,
    SavedCounterpartyContact,
    SavedCounterpartyFields,
    savedCounterpartyDetails,
} from "./counterpartyDetailChanges";
import { SavedCounterpartyAddress } from "./counterpartyAddressMatch";
import {
    groupContactDetails,
    parsePartyAddress,
    splitPersonName,
} from "./extractedPartyFields";

interface ExistingRow {
    id: string;
    isPrimary: boolean;
}

export type CounterpartyDetailChangeAction = "merge" | "replace";

export interface WritableCounterpartyRecord {
    counterparty: SavedCounterpartyFields;
    contacts: Array<SavedCounterpartyContact & ExistingRow>;
    addresses: Array<SavedCounterpartyAddress & ExistingRow>;
}

export interface CounterpartyDetailChangeWrites {
    contactUpdates: Array<{
        contactId: string;
        body: UpdateCounterpartyContactBody;
    }>;
    contactCreates: CreateCounterpartyContactBody[];
    addressUpdates: Array<{
        addressId: string;
        body: UpdateCounterpartyAddressBody;
    }>;
    addressCreates: CreateCounterpartyAddressBody[];
}

function primaryRow(rows: ExistingRow[]): ExistingRow | undefined {
    return rows.find((row) => row.isPrimary) ?? rows[0];
}

function addressFields(raw: string): CreateCounterpartyAddressBody {
    const parsed = parsePartyAddress(raw);
    return {
        street: parsed.street ?? null,
        street2: parsed.street2 ?? null,
        city: parsed.city ?? null,
        state: parsed.state ?? null,
        postalCode: parsed.postalCode ?? null,
        country: parsed.country ?? null,
    };
}

export function counterpartyDetailChangeWrites({
    action,
    changes,
    contacts,
    addresses,
    companyName = null,
}: {
    action: CounterpartyDetailChangeAction;
    changes: CounterpartyDetailChanges;
    contacts: ExistingRow[];
    addresses: ExistingRow[];
    companyName?: string | null;
}): CounterpartyDetailChangeWrites {
    const primaryContact = primaryRow(contacts);
    const primaryAddress = primaryRow(addresses);
    const [firstAddress, ...extraAddresses] = changes.addresses;
    const contactGroups = groupContactDetails({
        emails: changes.emails,
        phoneNumbers: changes.phoneNumbers,
        contactNames: changes.contactNames,
        fallbackContactName: companyName,
        isFirstPrimary: action === "replace" || contacts.length === 0,
    });

    if (action === "merge") {
        return {
            contactUpdates: [],
            contactCreates: contactGroups.first
                ? [contactGroups.first, ...contactGroups.extras]
                : contactGroups.extras,
            addressUpdates: [],
            addressCreates: changes.addresses.map((address, index) => ({
                ...addressFields(address),
                isPrimary: addresses.length === 0 && index === 0,
            })),
        };
    }

    const [firstEmail] = changes.emails;
    const [firstPhone] = changes.phoneNumbers;
    const [firstContactName] = changes.contactNames;
    const contactUpdates: CounterpartyDetailChangeWrites["contactUpdates"] = [];
    const contactCreates: CreateCounterpartyContactBody[] = [];
    if (contactGroups.first && primaryContact) {
        contactUpdates.push({
            contactId: primaryContact.id,
            body: {
                ...(firstEmail ? { email: firstEmail } : {}),
                ...(firstPhone ? { phoneNumber: firstPhone } : {}),
                ...(firstContactName
                    ? {
                          ...splitPersonName(firstContactName),
                          title: null,
                          middleName: null,
                          suffix: null,
                      }
                    : {}),
            },
        });
    } else if (contactGroups.first) {
        contactCreates.push(contactGroups.first);
    }
    contactCreates.push(...contactGroups.extras);

    const addressUpdates: CounterpartyDetailChangeWrites["addressUpdates"] = [];
    const addressCreates: CreateCounterpartyAddressBody[] = [];
    if (firstAddress && primaryAddress) {
        addressUpdates.push({
            addressId: primaryAddress.id,
            body: addressFields(firstAddress),
        });
    } else if (firstAddress) {
        addressCreates.push({
            ...addressFields(firstAddress),
            isPrimary: true,
        });
    }
    for (const address of extraAddresses) {
        addressCreates.push({
            ...addressFields(address),
            isPrimary: false,
        });
    }

    return {
        contactUpdates,
        contactCreates,
        addressUpdates,
        addressCreates,
    };
}

export interface CounterpartyReplacePreviewRow<TBody> {
    replacesId: string | null;
    body: TBody;
}

export interface CounterpartyReplacePreview {
    removedContactIds: string[];
    removedAddressIds: string[];
    contacts: Array<
        CounterpartyReplacePreviewRow<CreateCounterpartyContactBody>
    >;
    addresses: Array<
        CounterpartyReplacePreviewRow<CreateCounterpartyAddressBody>
    >;
}

interface PreviewContact extends SavedCounterpartyContact, ExistingRow {
    label?: string | null;
}

interface PreviewAddress extends SavedCounterpartyAddress, ExistingRow {
    label?: string | null;
}

function replacedContactBody(
    contact: PreviewContact,
    body: UpdateCounterpartyContactBody,
): CreateCounterpartyContactBody {
    return {
        label: body.label !== undefined ? body.label : (contact.label ?? null),
        title: body.title !== undefined ? body.title : contact.title,
        firstName:
            body.firstName !== undefined ? body.firstName : contact.firstName,
        middleName:
            body.middleName !== undefined
                ? body.middleName
                : contact.middleName,
        lastName:
            body.lastName !== undefined ? body.lastName : contact.lastName,
        suffix: body.suffix !== undefined ? body.suffix : contact.suffix,
        email: body.email !== undefined ? body.email : contact.email,
        phoneNumber:
            body.phoneNumber !== undefined
                ? body.phoneNumber
                : contact.phoneNumber,
        isPrimary: contact.isPrimary,
    };
}

function replacedAddressBody(
    address: PreviewAddress,
    body: UpdateCounterpartyAddressBody,
): CreateCounterpartyAddressBody {
    return {
        label: body.label !== undefined ? body.label : (address.label ?? null),
        street: body.street !== undefined ? body.street : address.street,
        street2: body.street2 !== undefined ? body.street2 : address.street2,
        city: body.city !== undefined ? body.city : address.city,
        state: body.state !== undefined ? body.state : address.state,
        postalCode:
            body.postalCode !== undefined
                ? body.postalCode
                : address.postalCode,
        country: body.country !== undefined ? body.country : address.country,
        isPrimary: address.isPrimary,
    };
}

export function counterpartyReplacePreview({
    writes,
    contacts,
    addresses,
}: {
    writes: CounterpartyDetailChangeWrites;
    contacts: PreviewContact[];
    addresses: PreviewAddress[];
}): CounterpartyReplacePreview {
    const removedContactIds: string[] = [];
    const previewContacts: CounterpartyReplacePreview["contacts"] = [];
    for (const update of writes.contactUpdates) {
        const contact = contacts.find((row) => row.id === update.contactId);
        if (!contact) {
            continue;
        }
        removedContactIds.push(contact.id);
        previewContacts.push({
            replacesId: contact.id,
            body: replacedContactBody(contact, update.body),
        });
    }
    for (const body of writes.contactCreates) {
        previewContacts.push({ replacesId: null, body });
    }

    const removedAddressIds: string[] = [];
    const previewAddresses: CounterpartyReplacePreview["addresses"] = [];
    for (const update of writes.addressUpdates) {
        const address = addresses.find((row) => row.id === update.addressId);
        if (!address) {
            continue;
        }
        removedAddressIds.push(address.id);
        previewAddresses.push({
            replacesId: address.id,
            body: replacedAddressBody(address, update.body),
        });
    }
    for (const body of writes.addressCreates) {
        previewAddresses.push({ replacesId: null, body });
    }

    return {
        removedContactIds,
        removedAddressIds,
        contacts: previewContacts,
        addresses: previewAddresses,
    };
}

export function counterpartyDetailWritesForRecord({
    action,
    extracted,
    record,
}: {
    action: CounterpartyDetailChangeAction;
    extracted: ExtractedCounterpartyParty;
    record: WritableCounterpartyRecord;
}): CounterpartyDetailChangeWrites {
    return counterpartyDetailChangeWrites({
        action,
        changes: changedCounterpartyDetails({
            extracted,
            saved: savedCounterpartyDetails(record),
        }),
        contacts: record.contacts,
        addresses: record.addresses,
        companyName: record.counterparty.name,
    });
}
